import { BUILD_BILLING_NOTICE, requireBuildCapability, isBuildImage, protectBuildReservation, reserveBuildInput, type BuildInput } from '../../core/imageBuild'
import { checkDeposit, MIN_FIRST_DEPOSIT_MICRO, runwayMinutes } from '../../core/deposit'
import { amountForRunTime, defaultRunTime, RUN_TIME_CHOICES, SHORT_RUN_MINUTES, type RunTimeKey } from '../../core/runTime'
import type { SizeValues } from '../../core/sizing'
import { useCallback, useState } from 'react'
import type { PlaintextExposure } from '../../core/popularImages'
import { requiresAllowlist, type GuidedPort } from '../../core/guided'
import { PlaintextNotice } from '../shared/PlaintextNotice'

import { DirectRentalReview, type RunTimeChoice } from './DirectRentalReview'
import { directQuotePriceError, prepareDirectRental, type GroupPriceCeiling } from './prepareDirectRental'
import { PageHeader } from '../shared/PageHeader'
import { SignSheet } from '../signing/SignSheet'
import { RENTAL_PATH_COPY, type RentalPath } from '../../core/rental'
import type { RentalDraft } from '../../core/rentalDraft'
import type { QuoteWire } from '../../core/protocol/directRental'
import { offlineSigner } from '../../core/tx'
import type { Connection } from '../../core/wallet'
import { rememberDeployedSdl } from '../../core/deployedSdl'
import { rememberDeploymentLabel } from '../../core/deploymentLabels'
import { resumePathFor } from '../../core/draftOrigin'
import type { Account, ChainInfo } from '../../data/types'
import type { SignedTxController } from '../../data/types'

/**
 * The direct rental journey's last screen: get a price, show it, sign once.
 *
 * **One SDL string, used twice, owned by the app layer.** `generatedSdl` is a
 * required prop: the gateway computes it once, uses it for the quote request,
 * and passes the same string here for signing. The chain compares the
 * provider's signed group-spec hash against the group as stored, so two
 * `parseSdl` calls — or a default filled in between them — produce
 * `ErrQuoteSpecMismatch` *after* the tenant has approved the transaction.
 *
 * An earlier draft of this file generated the SDL itself while the quote was
 * requested a layer above. That reintroduced exactly the split it was written
 * to prevent, and the architecture guard that moved transport out of this file
 * is what surfaced it. Making the string a prop means there is one producer and
 * no way for the two halves to disagree.
 */

export interface DirectRentalPageProps {
  account: Account | null
  chain: ChainInfo | null
  connection: Connection | null
  signedTx: SignedTxController
  /** The provider chosen on the chooser. */
  provider?: string | undefined
  /** The path chosen on the chooser. */
  path?: RentalPath | undefined

  /**
   * Host offers that can serve this product, already filtered for freshness.
   *
   * Passed in rather than fetched here: `src/test/architecture.test.ts`
   * forbids a feature component from importing `data/endpoints` or holding a
   * query hook, and the rule is right — transport belongs in the app layer, so
   * a screen can be rendered in a test without a network at all. The first
   * draft of this file fetched its own data and the guard caught it.
   */
  candidates: Array<{ host_offer_id: string }>

  /**
   * The host offer this rental is for, as the app layer resolved it from the
   * URL or the stored draft — not simply the first candidate, which may be a
   * different machine. Undefined for custom capacity, which names no offer.
   */
  selectedOfferId?: string | undefined

  /**
   * Ask the provider for a price.
   *
   * Resolves with the quote or rejects with tenant-safe copy. The app layer
   * owns the request; this screen owns what the tenant sees while it happens.
   */
  onRequestQuote?: (() => Promise<void>) | undefined

  /**
   * Called once the rental transaction has been accepted.
   *
   * The app layer uses it to drop the stored draft. Done on success only: a
   * rejected transaction leaves the tenant on this screen, and clearing their
   * selection underneath them would send them back to the chooser to re-pick
   * the host they already chose.
   */
  onLeased?: (() => void) | undefined

  /** Tenant-safe copy for a refused quote, from the app layer. */
  quoteError?: string | null | undefined

  /** The signed quote, once the app layer has one. */
  quote?: QuoteWire | null | undefined

  /**
   * The SDL the quote was requested with, and the one that will be signed.
   *
   * Required, and required to be the SAME string the gateway used for
   * `quoteRequestFor`. See the module comment for what happens when it is not.
   */
  generatedSdl: string
  buildInput?: BuildInput | undefined

  /** The parsed GroupSpec.Price() ceiling used by the provider quote. */
  priceCeiling?: GroupPriceCeiling | null | undefined

  /** Resource summary for the review screen. */
  resources: string

  /** The requested size, for the review's resource tiles. */
  size?: SizeValues | undefined

  /** The draft's name and image, recorded in this browser once the rental succeeds. */
  label?: { name: string; image: string | null } | undefined

  /** A public port the image leaves unencrypted: warned again here, and approval waits for the Configure acknowledgement. */
  plaintext?: { exposure: PlaintextExposure; acknowledged: boolean } | undefined

  /** The guided ports, for the per-port access rows. */
  ports?: GuidedPort[] | undefined
  /**
   * Whether this provider advertises allowlist enforcement. Undefined when no
   * port is restricted, or while the provider's record is still loading.
   */
  providerEnforcesAllowlist?: boolean | undefined

  onNavigate?: ((path: string) => void) | undefined
}

export function DirectRentalPage({
  account,
  chain,
  connection,
  signedTx,
  provider,
  path,
  candidates,
  selectedOfferId,
  onRequestQuote,
  quoteError,
  quote,
  onLeased,
  generatedSdl,
  buildInput,
  priceCeiling,
  resources,
  size,
  label,
  plaintext,
  ports = [],
  providerEnforcesAllowlist,
  onNavigate,
}: DirectRentalPageProps) {
  // A restricted port on a host that would ignore the list is the one thing
  // this feature must never produce, so approval waits for a yes.
  const allowlistBlocked = requiresAllowlist({ ports }) && providerEnforcesAllowlist !== true
  const [busy, setBusy] = useState(false)
  const [txError, setTxError] = useState<unknown>(null)
  // Undefined until the tenant picks: the default (decision D2) follows the
  // quote and the wallet balance as they arrive, rather than freezing at
  // whatever was known on first render.
  const [picked, setPicked] = useState<RunTimeChoice | undefined>(undefined)
  const [customInput, setCustomInput] = useState('')
  // The amount the tenant confirmed as a short run. Holding the amount rather
  // than a boolean means any change of amount clears the confirmation.
  const [acknowledgedFor, setAcknowledgedFor] = useState<string | null>(null)

  const mode = path ? RENTAL_PATH_COPY[path].mode : undefined
  const height = Number(chain?.height ?? 0)
  const rate = quote ? { denom: chain?.denom || 'uddt', amount: quote.price.amount } : null
  // What the wallet can put into the deposit: the whole balance. The rental
  // carries no fee (the network's gas price is zero, ADR-003), so nothing is
  // held back for one, by the default or by the check.
  const available = account?.balance?.denom === (chain?.denom || 'uddt') && /^\d+$/.test(account.balance.amount) ? BigInt(account.balance.amount) : null
  const choice: RunTimeChoice = picked !== undefined ? picked : defaultRunTime(rate, available)
  const chosenHours = RUN_TIME_CHOICES.find((item) => item.key === choice)?.hours
  const depositInput = choice === 'custom' ? customInput : chosenHours !== undefined ? amountForRunTime(rate, chosenHours) ?? '' : ''
  const depositCheck = checkDeposit(depositInput, { minimum: MIN_FIRST_DEPOSIT_MICRO, available })
  // Enforced here as well as on the button: a run under an hour is signed
  // only once the tenant has confirmed that exact amount.
  const shortMinutes = depositCheck.ok ? runwayMinutes(depositCheck.micro, rate) : null
  const shortUnconfirmed = shortMinutes !== null && shortMinutes < SHORT_RUN_MINUTES && !(depositCheck.ok && acknowledgedFor === depositCheck.micro)

  /**
   * Retry, but never with a stale quote.
   *
   * `signedTx.retry` resubmits the last input verbatim — the same two messages,
   * including the provider's signed quote with its `expiry_height`. For an
   * ordinary deployment that is exactly right. For a direct rental it is a
   * trap: the most likely reason the transaction was rejected is that the quote
   * expired while the tenant was reading the wallet prompt, and resubmitting an
   * expired quote is a **guaranteed** on-chain rejection. The tenant would
   * approve again only to fail again, with no way to tell from the sheet why.
   *
   * So retry is only a resubmit while the quote is still live. Past expiry it
   * asks the provider for a fresh price instead, which is the only action that
   * can actually succeed.
   */
  const retry = useCallback(async () => {
    if (quote && height && height < Number(quote.expiry_height)) {
      return signedTx.retry?.() ?? null
    }
    setTxError(null)
    signedTx.reset()
    await onRequestQuote?.()
    return null
  }, [quote, height, signedTx, onRequestQuote])

  const approve = useCallback(async () => {
    if (!quote || !chain || !connection || !depositCheck.ok || shortUnconfirmed) return
    setBusy(true)
    setTxError(null)
    let stopReservation = () => undefined as void
    try {
      await requireBuildCapability(quote.provider, generatedSdl)
      const prepared = await prepareDirectRental({
        address: connection.address,
        chainId: chain.chain_id,
        denom: chain.denom || 'uddt',
        height,
        // The SAME string the quote was requested with.
        generatedSdl,
        quote,
        deposit: depositCheck.micro,
      })
      const captured = await reserveBuildInput(buildInput, connection.address, String(prepared.dseq), generatedSdl)
      const guard = protectBuildReservation(captured)
      stopReservation = guard.stop
      rememberDeployedSdl(connection.address, prepared.dseq, generatedSdl, captured)
      const signer = await offlineSigner(connection.wallet, chain)
      const result = await signedTx.submit({
        signer,
        address: connection.address,
        messages: prepared.messages,
        fee: prepared.fee,
        chainId: chain.chain_id,
        ...(captured ? { beforeBroadcast: async () => { await requireBuildCapability(quote.provider, generatedSdl); await guard.beforeBroadcast() } } : {}),
      })
      // Only a transaction that landed and succeeded is a rental. A rejected
      // or unconfirmed one keeps the drafts, so the tenant can try again.
      if (result.succeeded) onLeased?.()
      // The lease is created in this same transaction, so the instance can be
      // started now — as it is after choosing an offer.
      if (result.accepted && result.included && result.succeeded) {
        if (label) rememberDeploymentLabel(connection.address, String(prepared.dseq), label)
        onNavigate?.(`/app/instances/${encodeURIComponent(String(prepared.dseq))}?tab=access&start=1`)
      }
    } catch (err) {
      setTxError(err)
    } finally {
      stopReservation()
      setBusy(false)
    }
  }, [quote, chain, connection, height, generatedSdl, buildInput, signedTx, onLeased, onNavigate, depositCheck, shortUnconfirmed, label])

  if (!path || !mode || mode === 'RENTAL_MODE_COMPETITIVE') {
    // Reached with no direct choice recorded. Sending the tenant back to the
    // chooser is better than guessing a product for them.
    return (
      <section>
        <PageHeader back={{ label: 'Back', onClick: () => onNavigate?.('/app/rent') }} title="Choose how to rent" description="Pick what you want to buy before reviewing a price." />
        <p className="empty">
          This page reviews a fixed-price rental.{' '}
          <a href="/app/rent/path">Choose how to rent</a> first.
        </p>
      </section>
    )
  }

  // The offer the tenant chose, never merely the first one listed: with two
  // fresh offers, candidates[0] can be a different machine at a different size
  // and price. Custom capacity names no offer at all.
  const hostOffer = mode === 'RENTAL_MODE_DIRECT_CUSTOM'
    ? undefined
    : selectedOfferId || quote?.host_offer_id || candidates[0]?.host_offer_id || undefined

  const draft: RentalDraft = {
    path,
    mode,
    pricingSource: 'provider-quote',
    ...(provider !== undefined ? { provider } : {}),
    ...(hostOffer !== undefined ? { hostOfferId: hostOffer } : {}),
    ...(quote
      ? {
          quote: {
            // A custom-capacity quote names no host offer, and is still a quote.
            quoteId: quote.nonce.slice(0, 12),
            provider: quote.provider,
            hostOfferId: quote.host_offer_id,
            rentalMode: quote.rental_mode as RentalDraft['mode'],
            pricePerBlock: quote.price.amount,
            expiryHeight: Number(quote.expiry_height),
          },
        }
      : {}),
  }
  const priceError = quote ? directQuotePriceError(priceCeiling, quote) : null
  // Back to the Configure screen this quote was asked from: the same path and
  // host, and for a host product the same offer. A bare /app/rent/configure
  // would reopen the draft with no product chosen.
  const backToConfigure = () => onNavigate?.(resumePathFor({ path, host: provider, offer: hostOffer }))

  return (
    <>
      <PageHeader
        back={{ label: 'Back to Configure', onClick: backToConfigure }}
        eyebrow="Rent · Review"
        title={RENTAL_PATH_COPY[path].title}
        description="This price is the provider's, signed and held for a limited time. Check it before you approve."
      />

      {quoteError ? (
        <div className="notice notice-caution" role="alert">
          {quoteError}
        </div>
      ) : null}

      {/*
        Said plainly rather than implied: the tenant chose a provider, and the
        provider chose the machine. Inventing a machine picker here would imply
        a guarantee about which one they get that nothing makes until the quote
        is signed.
      */}
      {mode !== 'RENTAL_MODE_DIRECT_CUSTOM' ? (
        <p className="muted">
          This provider selected the machine. The quote binds it, so the machine
          cannot change after you approve.
        </p>
      ) : null}

      {plaintext ? <PlaintextNotice exposure={plaintext.exposure} acknowledged={plaintext.acknowledged} /> : null}

      {allowlistBlocked ? (
        <div className={`notice ${providerEnforcesAllowlist === false ? 'notice-caution' : 'notice-info'}`} role={providerEnforcesAllowlist === false ? 'alert' : 'status'}>
          {providerEnforcesAllowlist === false
            ? 'This provider doesn’t enforce IP restrictions, so it can’t host an instance with a restricted port. Choose another host, or set the port to Anyone on Configure.'
            : 'Checking that this provider enforces IP restrictions…'}
        </div>
      ) : null}

      {isBuildImage(label?.image) && <p className="notice notice-caution">{BUILD_BILLING_NOTICE}</p>}
      <DirectRentalReview
        draft={draft}
        height={height || undefined}
        resources={resources}
        priceDenom={chain?.denom}
        busy={busy || !account || !connection || Boolean(priceError) || plaintext?.acknowledged === false}
        approvalBlocked={allowlistBlocked}
        ports={ports}
        priceError={priceError}
        onApprove={() => void approve()}
        onRequote={() => { setTxError(null); void onRequestQuote?.() }}
        onBack={backToConfigure}
        size={size}
        runTime={{
          choice,
          onChoice: (next: RunTimeKey) => setPicked(next),
          custom: customInput,
          onCustom: (next: string) => { setPicked('custom'); setCustomInput(next) },
          check: depositCheck,
          available,
          acknowledged: depositCheck.ok && acknowledgedFor === depositCheck.micro,
          onAcknowledge: (next: boolean) => setAcknowledgedFor(next && depositCheck.ok ? depositCheck.micro : null),
        }}
      />

      {txError && signedTx.stage === 'idle' && <p className="error" role="alert">{txError instanceof Error ? txError.message : String(txError)}</p>}

      <SignSheet
        stage={signedTx.stage}
        receipt={signedTx.receipt}
        error={txError}
        onRetry={retry}
        onCheckAgain={signedTx.checkAgain}
        onClose={() => setTxError(null)}
      />
    </>
  )
}
